import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";

/* ---------- helpers ---------- */
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
const fmtMoney = (val) => {
  const n = Number(val);
  return isNaN(n) ? String(val ?? "-") : n.toFixed(2);
};
const toDateInput = (isoOrDateStr) => {
  if (!isoOrDateStr) return "";
  const d = new Date(isoOrDateStr);
  if (isNaN(d.getTime())) return "";
  // yyyy-MM-dd
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, { params });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewExpence() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // table data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // dialogs
  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    name: "",
    amount: "",
    description: "",
    expenseDate: "",
  });
  const [saving, setSaving] = React.useState(false);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);


  const fmtDatePure = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = iso.split("T")[0].split("-");
  return `${m}/${d}/${y}`;
};

  // fetch list (cache-busted)
  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await getJSON("/api/expense", { _: Date.now() });
    setLoading(false);
    if (error) {
      setToast({ open: true, msg: "Failed to load expenses", sev: "error" });
      return;
    }
    setRows(Array.isArray(data) ? data : []);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // columns (id, name, amount, expenseDate) + actions
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        // index among visible rows on the current page
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        // page & pageSize for absolute numbering across pages
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "id", headerName: "ID", width: 90, hide: true },
    { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
    {
      field: "amount",
      headerName: "Amount",
      width: 140,
      valueGetter: (params) => fmtMoney(params.row.amount),
    },
    {
      field: "expenseDate",
      headerName: "Expense Date",
      width: 160,
    valueGetter: (params) => fmtDatePure(params.row.expenseDate),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  setEditRow(row);
                  setEditValues({
                    name: row.name || "",
                    amount: row.amount ?? "",
                    description: row.description || "",
                    expenseDate: toDateInput(row.expenseDate),
                  });
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // grid look
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  // update (optimistic + refetch)
  const handleUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);

    // Build payload exactly as your API expects
    const payload = {
      name: String(editValues.name ?? "").trim(),
      amount: Number(editValues.amount), // number for PUT
      description: String(editValues.description ?? "").trim(),
      expenseDate: String(editValues.expenseDate ?? "").trim(), // "YYYY-MM-DD"
    };

    const { data, error } = await putJSON(
      `/api/expense/${editRow.id}`,
      payload
    );
    setSaving(false);

    if (error) {
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    // Your PUT returns the updated object directly
    const updated = data?.data || data;
    const merged = updated?.id ? updated : { id: editRow.id, ...payload };

    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? { ...r, ...merged } : r))
    );

    setToast({
      open: true,
      msg: "Expense updated successfully",
      sev: "success",
    });
    setEditOpen(false);
    setEditRow(null);

    // refresh to ensure createdAt/updatedAt reflect the server
    load();
  };

  // delete (optimistic + refetch)
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const { data, error } = await deleteJSON(`/api/expense/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));

    setToast({
      open: true,
      msg: data?.message || "Expense deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);

    // safety refresh
    load();
  };

  return (
    <>
      <Box m="20px">
        <Header title="EXPENSES" subtitle="View, edit, and manage expenses" />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* Details */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Expense Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Name" value={detailRow.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Amount" value={fmtMoney(detailRow.amount)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Expense Date"
                  value={toDateInput(detailRow.expenseDate)}
                />
              </Grid>
              <Grid item xs={12}>
                <LabelValue
                  label="Description"
                  value={detailRow.description || "-"}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Expense</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={editValues.name}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, name: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="Amount"
              type="number"
              value={editValues.amount}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, amount: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "0.01", min: 0 }}
            />
            <TextField
              label="Expense Date"
              type="date"
              value={editValues.expenseDate}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, expenseDate: e.target.value }))
              }
              fullWidth
              variant="filled"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Description"
              multiline
              minRows={3}
              value={editValues.description}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, description: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Expense</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
